import styled from '@emotion/styled';
import { useMemo } from 'react';
import { Icon, Button } from '../../../../components';

interface CounterProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
}

const CounterContainer = styled.div<{ disabled?: boolean }>`
  position: relative;
  width: 100%;
  height: 48px;
  border: 1px solid ${(props) => props.theme.colors.neutral.greyDark[800]};
  border-radius: ${(props) => props.theme.shape.borderRadiusLarge};
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'default')};

  @media (min-width: 768px) {
    min-width: 140px;
  }
`;

const LeftButtonBackground = styled.div<{ isAtMin: boolean }>`
  width: 48px;
  height: 100%;
  background: ${(props) => (props.isAtMin ? props.theme.colors.neutral.greyDark[900] : props.theme.colors.primary[1000])};
  border-radius: ${(props) => props.theme.shape.borderRadiusLarge} 0 0 ${(props) => props.theme.shape.borderRadiusLarge};
  transition: background 0.2s ease;
`;

const RightButtonBackground = styled.div<{ isAtMax: boolean }>`
  width: 48px;
  height: 100%;
  background: ${(props) => (props.isAtMax ? props.theme.colors.neutral.greyDark[900] : props.theme.colors.primary[1000])};
  border-radius: 0 ${(props) => props.theme.shape.borderRadiusLarge} ${(props) => props.theme.shape.borderRadiusLarge} 0;
  transition: background 0.2s ease;
`;

const CounterValue = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: ${(props) => props.theme.typography.h3.fontSize};
  font-weight: ${(props) => props.theme.typography.h3.fontWeight};
  color: ${(props) => props.theme.colors.primary[1000]};
  line-height: 1.3;
  z-index: 1;
`;

const CounterButton = styled(Button)`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 2;

  &:first-of-type {
    left: 0;
  }

  &:last-of-type {
    right: 0;
  }
`;

export const Counter = ({
  value,
  onChange,
  min = 1,
  max = 99,
  step = 1,
  disabled = false,
  className,
}: CounterProps) => {
  // Validar props
  const validatedProps = useMemo(() => {
    if (min >= max) {
      console.warn(`Counter: min (${min}) debe ser menor que max (${max}). Usando defaults.`);
      return { min: 1, max: 99, value: Math.max(1, Math.min(99, value)) };
    }

    const clampedValue = Math.max(min, Math.min(max, value));
    if (clampedValue !== value) {
      console.warn(`Counter: value (${value}) fuera de rango [${min}, ${max}]. Ajustando a ${clampedValue}.`);
    }

    return { min, max, value: clampedValue };
  }, [min, max, value]);

  const increment = () => {
    if (validatedProps.value < validatedProps.max) {
      onChange(validatedProps.value + step);
    }
  };

  const decrement = () => {
    if (validatedProps.value > validatedProps.min) {
      onChange(validatedProps.value - step);
    }
  };

  const isAtMin = validatedProps.value <= validatedProps.min;
  const isAtMax = validatedProps.value >= validatedProps.max;

  return (
    <CounterContainer className={className} disabled={disabled}>
      <LeftButtonBackground isAtMin={isAtMin} />

      <CounterButton
        onClick={decrement}
        disabled={disabled || isAtMin}
        ariaLabel="Decrementar"
      >
        <Icon name="minus" color={isAtMin ? 'disabled' : 'white'} size={24} />
      </CounterButton>

      <CounterValue>{validatedProps.value}</CounterValue>

      <CounterButton
        onClick={increment}
        disabled={disabled || isAtMax}
        ariaLabel="Incrementar"
      >
        <Icon name="plus" color="white" size={24} />
      </CounterButton>

      <RightButtonBackground isAtMax={isAtMax} />
    </CounterContainer>
  );
};
